/**
 * @module engine3d/structure
 * @summary Builds the steelwork that carries the upper levels — decks,
 *   columns, handrails and stairs.
 * @description
 *   Without this, equipment on an upper level just floats. The structure is
 *   what makes a multi-level layout legible: you can see WHY the silo is up
 *   there and how somebody would reach it.
 *
 *   Three rules decide what actually gets a platform, and all three exist
 *   because the naive version — one deck spanning every item on a level — looks
 *   wrong on a real drawing:
 *
 *     1. **Clusters, not floors.** Equipment on a level is grouped by
 *        proximity and each group gets its own platform. A plant is a few
 *        access platforms in different places, not one deck the size of the
 *        building.
 *     2. **Fittings need no platform.** An elbow, a valve or an instrument is
 *        carried by the pipe it sits in. Building a deck around one is absurd.
 *     3. **Nor does anything a machine already carries.** A hopper sitting
 *        straight on an extruder throat is supported by the extruder, so no
 *        steelwork is drawn round it.
 *
 *   Anything still unwanted can be switched off per level, or globally.
 *
 *   Decks are drawn as open grating rather than plate. That is what industrial
 *   access platforms actually are, and it means an upper floor never hides the
 *   machines underneath it — which a solid deck, viewed from any angle above
 *   the horizon, always would.
 */
import type { Level, Shape } from '../types';
import { S } from '../engine/state';
import type { MatKey } from './materials';
import { box, boxCentered, cylinder, cylinderX, cylinderZ, merge, rotateZ, translate, type Mesh } from './mesh';
import { getModelFor, depthForShape } from './models';
import { baseYOf } from './levels';
import { placeOf } from './placement';

export interface StructurePart {
  mesh: Mesh;
  mat: MatKey;
}

/** How far a deck extends past the equipment standing on it. */
const MARGIN = 30;
/** Deck thickness — the grating sits just under the level's nominal height. */
const DECK_T = 3;
/** Handrail height above the deck. */
const RAIL_H = 46;
/** Items closer than this share one platform. */
const CLUSTER_GAP = 95;
/** A platform smaller than this gets no stair — it is reached from elsewhere. */
const STAIR_MIN = 120;

/**
 * Pipe fittings, instruments and drawing furniture. None of these is a reason
 * to build a platform: they are carried by the pipe, the wall, or nothing.
 */
const FITTINGS = new Set([
  'elbow90', 'connectingtube', 'gatevalve', 'ballvalve', 'checkvalve', 'ctrlvalve',
  'butterflyvalve', 'solenoidvalve', 'prv', 'pgauge', 'tsensor', 'flowmeter',
  'levelswitch', 'staticmix', 'screenchange', 'diverter2', 'diverter3',
  'divertervalve', 'odgauge', 'eccmon',
  'zonebox', 'textlabel', 'processarrow', 'titleblock',
]);

interface Rect {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
}

/** Plan footprint of a shape in world coordinates. */
function rectOf(s: Shape): Rect {
  const p = placeOf(s);
  const d = depthForShape(s);
  // The model may be rotated, so use the larger extent on both axes rather
  // than risk a deck a machine overhangs.
  const r = Math.max(s.w, d) / 2;
  return { x0: p.x - r, x1: p.x + r, z0: p.z - r, z1: p.z + r };
}

const expand = (r: Rect, m: number): Rect => ({ x0: r.x0 - m, x1: r.x1 + m, z0: r.z0 - m, z1: r.z1 + m });
const overlaps = (a: Rect, b: Rect): boolean => a.x0 <= b.x1 && a.x1 >= b.x0 && a.z0 <= b.z1 && a.z1 >= b.z0;

/** Bounding rectangle of a cluster. */
function groupRect(group: Shape[]): Rect {
  const r: Rect = { x0: Infinity, x1: -Infinity, z0: Infinity, z1: -Infinity };
  for (const s of group) {
    const q = rectOf(s);
    r.x0 = Math.min(r.x0, q.x0);
    r.x1 = Math.max(r.x1, q.x1);
    r.z0 = Math.min(r.z0, q.z0);
    r.z1 = Math.max(r.z1, q.z1);
  }
  return r;
}

/**
 * True when this item is already carried by a machine below it — the classic
 * case being a hopper or feeder mounted straight onto an extruder throat.
 *
 * The test is deliberately narrow: the item's centre must sit over something on
 * a lower level whose top actually comes up to meet it. That catches the real
 * cases without suppressing a platform for equipment merely standing near
 * something tall.
 */
function carriedFromBelow(s: Shape, others: Shape[]): boolean {
  const myY = baseYOf(s);
  if (myY <= 0.5) return false;
  const p = placeOf(s);
  for (const o of others) {
    if (o === s || o.hide3 || FITTINGS.has(o.type)) continue;
    const oy = baseYOf(o);
    if (oy >= myY - 1) continue;
    const top = oy + getModelFor(o).height;
    // It has to reach up to us — within a machine's own tolerance.
    if (top < myY - 34) continue;
    const r = expand(rectOf(o), 6);
    if (p.x >= r.x0 && p.x <= r.x1 && p.z >= r.z0 && p.z <= r.z1) return true;
  }
  return false;
}

/** The shapes on a level that actually justify a platform. */
function supportedShapesOn(level: Level): Shape[] {
  const groundId = S.levels[0]?.id;
  return S.shapes.filter((s) => {
    if ((s.level ?? groundId) !== level.id) return false;
    // Hidden equipment has nothing to stand on, and an item told explicitly to
    // go without a platform goes without one — whatever its neighbours do.
    if (s.hide3 || s.noStruct3) return false;
    if (FITTINGS.has(s.type)) return false;
    return !carriedFromBelow(s, S.shapes);
  });
}

/** Group shapes whose footprints are within `CLUSTER_GAP` of each other. */
function cluster(shapes: Shape[]): Shape[][] {
  const rects = shapes.map(rectOf);
  const parent = shapes.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  for (let i = 0; i < shapes.length; i++)
    for (let k = i + 1; k < shapes.length; k++)
      if (overlaps(expand(rects[i], CLUSTER_GAP / 2), expand(rects[k], CLUSTER_GAP / 2))) {
        const a = find(i);
        const b = find(k);
        if (a !== b) parent[a] = b;
      }
  const groups = new Map<number, Shape[]>();
  shapes.forEach((s, i) => {
    const root = find(i);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)!.push(s);
  });
  return [...groups.values()];
}

/** Posts + two rails along one edge of a deck. */
function handrail(ax: 'x' | 'z', from: number, to: number, fixed: number, y: number): { posts: Mesh; rails: Mesh } {
  const len = Math.abs(to - from);
  const n = Math.max(2, Math.round(len / 70));
  const posts: Mesh[] = [];
  for (let i = 0; i <= n; i++) {
    const t = from + ((to - from) * i) / n;
    const x = ax === 'x' ? t : fixed;
    const z = ax === 'x' ? fixed : t;
    posts.push(translate(boxCentered(2.2, RAIL_H, 2.2), x, y + RAIL_H / 2, z));
  }
  const mid = (from + to) / 2;
  const rails: Mesh[] = [];
  for (const h of [RAIL_H, RAIL_H * 0.55])
    rails.push(
      ax === 'x'
        ? translate(cylinderX(1.4, len, 8), mid, y + h, fixed)
        : translate(cylinderZ(1.4, len, 8), fixed, y + h, mid),
    );
  return { posts: merge(...posts), rails: merge(...rails) };
}

const EMPTY: Mesh = { pos: [], nrm: [], idx: [] };

/**
 * A straight stair run climbing from `yFrom` up to `yTo`, its TOP landing at
 * `x` and its foot out at `x - run`.
 *
 * Which way round that is matters and is easy to get backwards: a stair has to
 * arrive at the platform. Pitched the other way it rises away from the deck and
 * finishes in mid-air, which looks like access but reaches nothing.
 */
function stair(x: number, z: number, yFrom: number, yTo: number): { treads: Mesh; rails: Mesh } {
  const rise = yTo - yFrom;
  if (rise < 8) return { treads: EMPTY, rails: EMPTY };
  const steps = Math.max(3, Math.round(rise / 9));
  const run = rise * 0.85;
  const foot = x - run;
  const treads: Mesh[] = [];
  for (let i = 0; i < steps; i++) {
    const t = (i + 1) / steps;
    // Climbing toward +X, so the last tread is flush with the deck edge.
    treads.push(
      translate(box(run / steps + 1.5, 1.6, 26), foot + run * t - run / (steps * 2), yFrom + rise * t, z),
    );
  }
  const rails: Mesh[] = [];
  const len = Math.hypot(run, rise);
  for (const dz of [-13, 13]) {
    for (let i = 0; i <= steps; i += Math.max(1, Math.round(steps / 4))) {
      const t = i / steps;
      rails.push(translate(boxCentered(2, RAIL_H, 2), foot + run * t, yFrom + rise * t + RAIL_H / 2, z + dz));
    }
    // A rail raked to the pitch, so the run reads as a stair and not as a row
    // of bollards standing on steps.
    rails.push(
      translate(
        rotateZ(boxCentered(len, 2.4, 2.4), Math.atan2(rise, run)),
        foot + run / 2,
        (yFrom + yTo) / 2 + RAIL_H,
        z + dz,
      ),
    );
  }
  return { treads: merge(...treads), rails: merge(...rails) };
}

let visible = true;

/** Show or hide all access steelwork (the HUD's "Structure" toggle). */
export function setStructureVisible(v: boolean): void {
  visible = v;
}
export function isStructureVisible(): boolean {
  return visible;
}

/** Toggle the steelwork for one floor. */
export function setLevelStructure(id: string, on: boolean): void {
  const l = S.levels.find((x) => x.id === id);
  if (!l) return;
  if (on) delete l.structure;
  else l.structure = false;
}

/** True when a level would draw any steelwork at all, given the rules above. */
export function levelHasStructure(level: Level): boolean {
  if (level.structure === false) return false;
  if ((S.levels[0]?.id ?? '') === level.id) return false;
  return supportedShapesOn(level).length > 0;
}

/** One planned platform: which floor it belongs to and the ground it covers. */
export interface Deck {
  levelId: string;
  /** Floor height of the deck's walking surface. */
  y: number;
  /** Height of the level below, where its columns land. */
  below: number;
  rect: Rect;
  /** True for the platform that gets the stair on its level. */
  stair: boolean;
}

/**
 * Work out where platforms go, without building any geometry.
 *
 * Separating the decision from the modelling is what makes the three rules in
 * the module header testable — and the rules are the part that decides whether
 * the drawing looks right.
 */
export function planDecks(): Deck[] {
  const levels = S.levels;
  if (levels.length < 2) return [];
  const out: Deck[] = [];
  for (let li = 1; li < levels.length; li++) {
    const level = levels[li];
    if (level.structure === false) continue;
    const carried = supportedShapesOn(level);
    if (!carried.length) continue;
    const below = levels[li - 1].elevation;
    const groups = cluster(carried);

    // Only the biggest platform on a level gets a stair; one per cluster turns
    // the drawing into a staircase showroom.
    let biggest = 0;
    let biggestArea = -1;
    groups.forEach((g, i) => {
      const r = groupRect(g);
      const area = (r.x1 - r.x0) * (r.z1 - r.z0);
      if (area > biggestArea) {
        biggestArea = area;
        biggest = i;
      }
    });

    groups.forEach((group, gi) => {
      const rect = expand(groupRect(group), MARGIN);
      out.push({
        levelId: level.id,
        y: level.elevation,
        below,
        rect,
        stair: gi === biggest && Math.max(rect.x1 - rect.x0, rect.z1 - rect.z0) >= STAIR_MIN,
      });
    });
  }
  return out;
}

/**
 * Build all the steelwork for the current document.
 * Returns world-space geometry — decks are per-level absolute positions, not
 * something that can be expressed as a local model plus a transform.
 */
export function buildStructure(): StructurePart[] {
  if (!visible) return [];

  const gratings: Mesh[] = [];
  const edges: Mesh[] = [];
  const columns: Mesh[] = [];
  const rails: Mesh[] = [];
  const posts: Mesh[] = [];
  const treads: Mesh[] = [];

  for (const deck of planDecks()) {
    {
      const { y, below } = deck;
      const r = deck.rect;
      const w = r.x1 - r.x0;
      const d = r.z1 - r.z0;
      const cx = (r.x0 + r.x1) / 2;
      const cz = (r.z0 + r.z1) / 2;

      // Deck: grating panel with a solid edge beam, so the platform reads as a
      // built thing rather than a floating translucent rectangle.
      gratings.push(translate(box(w, DECK_T, d), cx, y - DECK_T, cz));
      edges.push(
        merge(
          translate(box(w, DECK_T * 2.2, 3), cx, y - DECK_T * 1.6, r.z0 + 1.5),
          translate(box(w, DECK_T * 2.2, 3), cx, y - DECK_T * 1.6, r.z1 - 1.5),
          translate(box(3, DECK_T * 2.2, d), r.x0 + 1.5, y - DECK_T * 1.6, cz),
          translate(box(3, DECK_T * 2.2, d), r.x1 - 1.5, y - DECK_T * 1.6, cz),
          translate(box(w, 7, 1.6), cx, y, r.z0 + 0.8),
          translate(box(w, 7, 1.6), cx, y, r.z1 - 0.8),
          translate(box(1.6, 7, d), r.x0 + 0.8, y, cz),
        ),
      );

      // Perimeter columns down to the level below. Interior ones would spear
      // whatever is standing on that floor.
      const nx = Math.max(1, Math.round(w / 190));
      const nz = Math.max(1, Math.round(d / 190));
      for (let i = 0; i <= nx; i++)
        for (let k = 0; k <= nz; k++) {
          if (i > 0 && i < nx && k > 0 && k < nz) continue;
          columns.push(
            translate(cylinder(3.4, y - DECK_T - below, { seg: 8 }), r.x0 + (w * i) / nx, below, r.z0 + (d * k) / nz),
          );
        }

      for (const [ax, a, b, fixed] of [
        ['x', r.x0, r.x1, r.z0 + 1.5],
        ['x', r.x0, r.x1, r.z1 - 1.5],
        ['z', r.z0, r.z1, r.x1 - 1.5],
      ] as ['x' | 'z', number, number, number][]) {
        const hr = handrail(ax, a, b, fixed, y);
        posts.push(hr.posts);
        rails.push(hr.rails);
      }

      if (deck.stair) {
        const st = stair(r.x0 - 2, cz, below, y);
        treads.push(st.treads);
        posts.push(st.rails);
      }
    }
  }

  const parts: StructurePart[] = [];
  const push = (meshes: Mesh[], mat: MatKey) => {
    if (meshes.length) parts.push({ mesh: merge(...meshes), mat });
  };
  push(columns, 'column');
  push(edges, 'deck');
  push(treads, 'deck');
  push(posts, 'galv');
  push(rails, 'safety');
  // Grating last: it is translucent, so the painter's sort wants it after the
  // solid steel it sits on.
  push(gratings, 'grating');
  return parts;
}

/** Total height of the tallest model on a level — used when spacing floors. */
export function tallestOnLevel(levelId: string): number {
  let max = 0;
  for (const s of S.shapes) {
    if ((s.level ?? S.levels[0]?.id) !== levelId) continue;
    const h = getModelFor(s).height;
    if (h > max) max = h;
  }
  return max;
}
